import React, { useState, useEffect, useMemo } from 'react';
import './App.css';
import { restaurants } from './data/restaurantsData';
import { categories, locations, alphabetSections, sortOptions } from './data/categories';
import { getMenuForCategory } from './data/menuItems';
import { ThemeProvider, useTheme } from './ThemeContext';
import SettingsView from './SettingsView';
// import { supabaseHelpers } from './supabase'; // Now using Worker API

// Multi-language support
const translations = {
  en: {
    appName: 'MemsEat',
    search: 'Search for restaurants, cuisines...',
    categories: 'Categories',
    viewAll: 'View All',
    featuredOffers: 'Featured Offers',
    restaurants: 'Restaurants',
    new: 'New',
    location: 'Location',
    groups: 'Groups',
    view: 'View',
    tree: 'Tree',
    all: 'All',
    coupon: 'Coupon',
    shop: 'Shop',
    filters: 'Filters',
    category: 'Category',
    priceRange: 'Price Range',
    sortBy: 'Sort By',
    allPrices: 'All Prices',
    budget: '$ - Budget',
    moderate: '$$ - Moderate',
    premium: '$$$ - Premium',
    addToCart: 'Add to Cart',
    cart: 'Cart',
    favorites: 'Favorites',
    menu: 'Menu',
    back: 'Back',
    clearCart: 'Clear Cart',
    checkout: 'Checkout',
    total: 'Total',
    quantity: 'Quantity',
    remove: 'Remove',
    popular: 'Popular',
    featured: 'Featured',
    off: 'OFF',
    min: 'min',
    delivery: 'Delivery',
    rating: 'Rating',
    reviews: 'reviews',
    distance: 'Distance',
    deliveryTime: 'Delivery Time',
    price: 'Price',
    topChart: 'Top Chart',
    emptyCart: 'Your cart is empty',
    startShopping: 'Start Shopping',
    emptyFavorites: 'No favorites yet',
    browseFavorites: 'Browse Restaurants',
    welcome: 'Hi, Ellis Chan!',
    bookingTitle: 'Booking',
    moreOptions: 'More Options',
    confirm: 'Confirm',
    options: 'Options',
    settings: 'Settings',
    customizeYourExperience: 'Customize your experience',
    themeSelection: 'Theme Selection',
    chooseYourStyle: 'Choose your preferred style',
    kidFriendlyDesc: 'Playful and colorful design',
    professionalDesc: 'Clean and modern business style',
    rockRollDesc: 'Bold and edgy rock style',
    blueDesc: 'Calm and peaceful blue tones',
    romanceDesc: 'Elegant and romantic design',
    currentTheme: 'Current Theme',
    language: 'Language',
    selectLanguage: 'Select your preferred language',
    currentLanguage: 'Current Language',
    languageNote: 'Language settings available in header',
    preferences: 'Preferences',
    appPreferences: 'Application preferences',
    notifications: 'Notifications',
    notificationDesc: 'Receive order updates',
    soundEffects: 'Sound Effects',
    soundDesc: 'Play sounds for interactions',
    currentlyUsing: 'Currently Using',
    orderType: 'Order Type',
    dineIn: 'Dine-in',
    takeaway: 'Takeaway',
    selectOrderType: 'Order for Takeaway',
    orderNow: 'Order Takeaway',
    orderTypeDesc: 'Order food for pickup and takeaway',
    takeawayOrder: 'Takeaway Order',
    pickupOrder: 'Pickup Order',
    bookPickup: 'Book Pickup',
    reservePickup: 'Reserve Pickup',
    pickupBooking: 'Pickup Booking',
    bookNow: 'Book Now',
    reserveNow: 'Reserve Now',
    customerName: 'Customer Name',
    phoneNumber: 'Phone Number',
    pickupTime: 'Pickup Time',
    specialInstructions: 'Special Instructions',
    bookingDetails: 'Booking Details',
    confirmBooking: 'Confirm Booking',
    bookingSuccess: 'Booking Confirmed!',
    bookingSuccessMsg: 'Your pickup has been booked successfully. You will receive a confirmation message.',
    themeApplied: 'This theme is applied across all pages and components in the app.'
  },
  zh: {
    appName: 'MemsEat',
    search: '搜尋餐廳、菜式...',
    categories: '分類',
    viewAll: '查看全部',
    featuredOffers: '精選優惠',
    restaurants: '餐廳',
    new: '新店',
    location: '位置',
    groups: '群組',
    view: '檢視',
    tree: '樹狀',
    all: '全部',
    coupon: '優惠券',
    shop: '商店',
    filters: '篩選',
    category: '分類',
    priceRange: '價格範圍',
    sortBy: '排序',
    allPrices: '所有價格',
    budget: '$ - 經濟',
    moderate: '$$ - 中等',
    premium: '$$$ - 高級',
    addToCart: '加入購物車',
    cart: '購物車',
    favorites: '收藏',
    menu: '菜單',
    back: '返回',
    clearCart: '清空購物車',
    checkout: '結帳',
    total: '總計',
    quantity: '數量',
    remove: '移除',
    popular: '熱門',
    featured: '精選',
    off: '折扣',
    min: '分鐘',
    delivery: '送貨',
    rating: '評分',
    reviews: '評論',
    distance: '距離',
    deliveryTime: '送貨時間',
    price: '價格',
    topChart: '排行榜',
    emptyCart: '購物車是空的',
    startShopping: '開始購物',
    emptyFavorites: '還沒有收藏',
    browseFavorites: '瀏覽餐廳',
    welcome: '你好，Ellis Chan！',
    bookingTitle: '預訂',
    moreOptions: '更多選項',
    confirm: '確定',
    options: '選項',
    settings: '設定',
    customizeYourExperience: '自訂您的體驗',
    themeSelection: '主題選擇',
    chooseYourStyle: '選擇您喜歡的風格',
    kidFriendlyDesc: '有趣和豐富多彩的設計',
    professionalDesc: '簡潔現代的商務風格',
    rockRollDesc: '大膽前衛的搖滾風格',
    blueDesc: '平靜祥和的藍色調',
    romanceDesc: '優雅浪漫的設計',
    currentTheme: '當前主題',
    language: '語言',
    selectLanguage: '選擇您的首選語言',
    currentLanguage: '當前語言',
    languageNote: '語言設定可在標題中找到',
    preferences: '偏好設定',
    appPreferences: '應用程式偏好設定',
    notifications: '通知',
    notificationDesc: '接收訂單更新',
    soundEffects: '音效',
    soundDesc: '播放互動音效',
    currentlyUsing: '目前使用',
    orderType: '訂單類型',
    dineIn: '堂食',
    takeaway: '外帶',
    selectOrderType: '外帶訂購',
    orderNow: '立即外帶',
    orderTypeDesc: '訂購食物外帶取餐',
    takeawayOrder: '外帶訂單',
    pickupOrder: '取餐訂單',
    bookPickup: '預約取餐',
    reservePickup: '預訂取餐',
    pickupBooking: '取餐預約',
    bookNow: '立即預約',
    reserveNow: '立即預訂',
    customerName: '客戶姓名',
    phoneNumber: '電話號碼',
    pickupTime: '取餐時間',
    specialInstructions: '特殊要求',
    bookingDetails: '預約詳情',
    confirmBooking: '確認預約',
    bookingSuccess: '預約成功！',
    bookingSuccessMsg: '您的取餐預約已成功確認，您將收到確認訊息。',
    themeApplied: '此主題適用於應用程式中的所有頁面和組件。'
  },
  ja: {
    appName: 'MemsEat',
    search: 'レストラン、料理を検索...',
    categories: 'カテゴリー',
    viewAll: 'すべて表示',
    featuredOffers: 'おすすめオファー',
    restaurants: 'レストラン',
    new: '新着',
    location: '場所',
    groups: 'グループ',
    view: '表示',
    tree: 'ツリー',
    all: 'すべて',
    coupon: 'クーポン',
    shop: 'ショップ',
    filters: 'フィルター',
    category: 'カテゴリー',
    priceRange: '価格帯',
    sortBy: '並び替え',
    allPrices: 'すべての価格',
    budget: '$ - エコノミー',
    moderate: '$$ - スタンダード',
    premium: '$$$ - プレミアム',
    addToCart: 'カートに追加',
    cart: 'カート',
    favorites: 'お気に入り',
    menu: 'メニュー',
    back: '戻る',
    clearCart: 'カートをクリア',
    checkout: 'チェックアウト',
    total: '合計',
    quantity: '数量',
    remove: '削除',
    popular: '人気',
    featured: 'おすすめ',
    off: 'オフ',
    min: '分',
    delivery: '配達',
    rating: '評価',
    reviews: 'レビュー',
    distance: '距離',
    deliveryTime: '配達時間',
    price: '価格',
    topChart: 'トップチャート',
    emptyCart: 'カートは空です',
    startShopping: 'ショッピング開始',
    emptyFavorites: 'お気に入りはありません',
    browseFavorites: 'レストランを閲覧',
    welcome: 'こんにちは、Ellis Chan！',
    bookingTitle: '予約',
    moreOptions: 'その他のオプション',
    confirm: '確認',
    options: 'オプション',
    settings: '設定',
    customizeYourExperience: 'エクスペリエンスをカスタマイズ',
    themeSelection: 'テーマ選択',
    chooseYourStyle: 'お好みのスタイルを選択',
    kidFriendlyDesc: '楽しくカラフルなデザイン',
    professionalDesc: 'クリーンでモダンなビジネススタイル',
    rockRollDesc: '大胆でエッジの効いたロックスタイル',
    blueDesc: '穏やかで平和的なブルートーン',
    romanceDesc: 'エレガントでロマンチックなデザイン',
    currentTheme: '現在のテーマ',
    language: '言語',
    selectLanguage: 'お好みの言語を選択',
    currentLanguage: '現在の言語',
    languageNote: '言語設定はヘッダーで利用可能',
    preferences: '設定',
    appPreferences: 'アプリケーション設定',
    notifications: '通知',
    notificationDesc: '注文の更新を受け取る',
    soundEffects: '効果音',
    soundDesc: 'インタラクションの音を再生',
    currentlyUsing: '現在使用中',
    orderType: '注文タイプ',
    dineIn: '店内',
    takeaway: 'テイクアウト',
    selectOrderType: 'テイクアウト注文',
    orderNow: 'テイクアウト注文',
    orderTypeDesc: 'テイクアウト用の料理を注文',
    takeawayOrder: 'テイクアウト注文',
    pickupOrder: '受け取り注文',
    bookPickup: '受け取り予約',
    reservePickup: '受け取り予約',
    pickupBooking: '受け取り予約',
    bookNow: '今すぐ予約',
    reserveNow: '今すぐ予約',
    customerName: 'お客様名',
    phoneNumber: '電話番号',
    pickupTime: '受け取り時間',
    specialInstructions: '特別な指示',
    bookingDetails: '予約詳細',
    confirmBooking: '予約確認',
    bookingSuccess: '予約完了！',
    bookingSuccessMsg: '受け取り予約が正常に完了しました。確認メッセージをお送りします。',
    themeApplied: 'このテーマはアプリ内のすべてのページとコンポーネントに適用されます。'
  },
  ko: {
    appName: 'MemsEat',
    search: '레스토랑, 요리 검색...',
    categories: '카테고리',
    viewAll: '모두 보기',
    featuredOffers: '추천 오퍼',
    restaurants: '레스토랑',
    new: '신규',
    location: '위치',
    groups: '그룹',
    view: '보기',
    tree: '트리',
    all: '전체',
    coupon: '쿠폰',
    shop: '쇼핑',
    filters: '필터',
    category: '카테고리',
    priceRange: '가격대',
    sortBy: '정렬',
    allPrices: '모든 가격',
    budget: '$ - 저렴',
    moderate: '$$ - 보통',
    premium: '$$$ - 고급',
    addToCart: '장바구니 담기',
    cart: '장바구니',
    favorites: '즐겨찾기',
    menu: '메뉴',
    back: '뒤로',
    clearCart: '장바구니 비우기',
    checkout: '결제',
    total: '총계',
    quantity: '수량',
    remove: '제거',
    popular: '인기',
    featured: '추천',
    off: '할인',
    min: '분',
    delivery: '배달',
    rating: '평점',
    reviews: '리뷰',
    distance: '거리',
    deliveryTime: '배달시간',
    price: '가격',
    topChart: '인기순위',
    emptyCart: '장바구니가 비어있습니다',
    startShopping: '쇼핑 시작',
    emptyFavorites: '즐겨찾기가 없습니다',
    browseFavorites: '레스토랑 둘러보기',
    welcome: '안녕하세요, Ellis Chan!',
    bookingTitle: '예약',
    moreOptions: '더 많은 옵션',
    confirm: '확인',
    options: '옵션',
    settings: '설정',
    customizeYourExperience: '경험을 맞춤설정하세요',
    themeSelection: '테마 선택',
    chooseYourStyle: '선호하는 스타일을 선택하세요',
    kidFriendlyDesc: '재미있고 다채로운 디자인',
    professionalDesc: '깔끔하고 현대적인 비즈니스 스타일',
    rockRollDesc: '대담하고 날카로운 록 스타일',
    blueDesc: '차분하고 평화로운 블루 톤',
    romanceDesc: '우아하고 로맨틱한 디자인',
    currentTheme: '현재 테마',
    language: '언어',
    selectLanguage: '선호하는 언어를 선택하세요',
    currentLanguage: '현재 언어',
    languageNote: '언어 설정은 헤더에서 사용 가능',
    preferences: '환경설정',
    appPreferences: '애플리케이션 환경설정',
    notifications: '알림',
    notificationDesc: '주문 업데이트 받기',
    soundEffects: '효과음',
    soundDesc: '상호작용 소리 재생',
    currentlyUsing: '현재 사용 중',
    orderType: '주문 유형',
    dineIn: '매장 식사',
    takeaway: '테이크아웃',
    selectOrderType: '테이크아웃 주문',
    orderNow: '테이크아웃 주문',
    orderTypeDesc: '테이크아웃용 음식을 주문하세요',
    takeawayOrder: '테이크아웃 주문',
    pickupOrder: '픽업 주문',
    bookPickup: '픽업 예약',
    reservePickup: '픽업 예약',
    pickupBooking: '픽업 예약',
    bookNow: '지금 예약',
    reserveNow: '지금 예약',
    customerName: '고객명',
    phoneNumber: '전화번호',
    pickupTime: '픽업 시간',
    specialInstructions: '특별 지시사항',
    bookingDetails: '예약 세부사항',
    confirmBooking: '예약 확인',
    bookingSuccess: '예약 완료!',
    bookingSuccessMsg: '픽업 예약이 성공적으로 완료되었습니다. 확인 메시지를 받으실 것입니다.',
    themeApplied: '이 테마는 앱의 모든 페이지와 구성 요소에 적용됩니다.'
  }
};

const FoodDirectoryAppContent = () => {
  const { theme } = useTheme();
  const [currentView, setCurrentView] = useState('directory');
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [cart, setCart] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [sortBy, setSortBy] = useState('distance');
  const [priceFilter, setPriceFilter] = useState('all');
  const [favorites, setFavorites] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [alphabetFilter, setAlphabetFilter] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [language, setLanguage] = useState('en');
  const [selectedMenuItem, setSelectedMenuItem] = useState(null);
  const [showItemOptions, setShowItemOptions] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [restaurantsData, setRestaurantsData] = useState(restaurants);
  const [orderType, setOrderType] = useState('takeaway'); // MemsEat is for takeaway orders only
  const [showOrderTypeSelector, setShowOrderTypeSelector] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [bookingData, setBookingData] = useState({
    name: '',
    phone: '',
    pickupTime: '',
    specialInstructions: ''
  });
  const [currentUser] = useState({
    name: 'Ellis Chan',
    phone: '28868888',
    id: '4',
    avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face',
    location: { lat: 22.3193, lng: 114.1694 }
  });

  const t = translations[language];

  // Takeaway order functions
  const handleOrderNow = (restaurant) => {
    // Generate MemsOrder URL for takeaway orders with table 0
    const memsOrderUrl = `https://memsorder-app.ymanc96868126.workers.dev/?restaurant=${restaurant.id}&table=0&order_type=takeaway`;
    window.open(memsOrderUrl, '_blank');
  };

  const showOrderTypeModal = (restaurant) => {
    setSelectedRestaurant(restaurant);
    setShowOrderTypeSelector(true);
  };

  // Booking functions
  const handleBookingChange = (field, value) => {
    setBookingData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleBookPickup = (restaurant) => {
    setSelectedRestaurant(restaurant);
    setBookingData({
      name: currentUser.name,
      phone: currentUser.phone,
      pickupTime: '',
      specialInstructions: ''
    });
    setShowBookingModal(true);
  };

  const confirmBooking = async () => {
    if (!bookingData.name || !bookingData.phone) {
      alert('Please fill in your name and phone number');
      return;
    }

    try {
      // Here you would typically send the booking to your backend
      // For now, we'll just show a success message
      alert(`${t.bookingSuccess}\n\n${t.bookingSuccessMsg}`);
      setShowBookingModal(false);
      setBookingData({
        name: '',
        phone: '',
        pickupTime: '',
        specialInstructions: ''
      });
    } catch (error) {
      console.error('Booking error:', error);
      alert('Failed to create booking. Please try again.');
    }
  };

  // Enhanced categories with counts
  const categoriesWithAll = useMemo(() => {
    const allCategories = [
      { id: 'all', name: t.all, emoji: '🍽️', chineseName: '全部' },
      ...categories
    ];
    
    return allCategories.map(category => ({
      ...category,
      count: category.id === 'all' 
        ? restaurantsData.length 
        : restaurantsData.filter(r => r.category === category.id).length
    }));
  }, [t.all, restaurantsData]);

  // Enhanced locations with counts
  const locationsWithAll = useMemo(() => {
    const allLocations = [
      { id: 'all', name: 'All Areas', chineseName: '全部地區' },
      ...locations
    ];
    
    return allLocations.map(location => ({
      ...location,
      count: location.id === 'all' 
        ? restaurantsData.length 
        : restaurantsData.filter(r => r.location === location.id).length
    }));
  }, [restaurantsData]);

  // Initialize app with database data
  useEffect(() => {
    const initializeApp = async () => {
      try {
        // Load restaurants from database via Worker API
        const response = await fetch('/api/restaurants');
        if (response.ok) {
          const dbRestaurants = await response.json();
          setRestaurantsData(dbRestaurants);
        }
        
        setFavorites(['rest001', 'rest003', 'rest004', 'rest012', 'rest015']);
        setIsLoading(false);
      } catch (err) {
        console.error('Error loading restaurants:', err);
        setIsLoading(false);
      }
    };

    initializeApp();
  }, []);

  // Enhanced filtering logic
  const filteredRestaurants = useMemo(() => {
    let filtered = restaurantsData;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(restaurant =>
        restaurant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        restaurant.chineseName.includes(searchTerm) ||
        restaurant.cuisine.some(c => c.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Category filter
    if (selectedCategory && selectedCategory !== 'all') {
      filtered = filtered.filter(restaurant => restaurant.category === selectedCategory);
    }

    // Location filter
    if (selectedLocation && selectedLocation !== 'all') {
      filtered = filtered.filter(restaurant => restaurant.location === selectedLocation);
    }

    // Price filter
    if (priceFilter && priceFilter !== 'all') {
      filtered = filtered.filter(restaurant => restaurant.priceRange === priceFilter);
    }

    // Alphabet filter
    if (alphabetFilter && alphabetFilter !== 'all') {
      if (alphabetFilter === '0-9') {
        filtered = filtered.filter(restaurant => /^[0-9]/.test(restaurant.name));
      } else {
        filtered = filtered.filter(restaurant => 
          restaurant.name.toUpperCase().startsWith(alphabetFilter)
        );
      }
    }

    // Sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'distance':
          return a.distance - b.distance;
        case 'rating':
          return b.rating - a.rating;
        case 'deliveryTime':
          return parseInt(a.deliveryTime.split('-')[0]) - parseInt(b.deliveryTime.split('-')[0]);
        case 'price':
          const priceOrder = { '$': 1, '$$': 2, '$$$': 3 };
          return priceOrder[a.priceRange] - priceOrder[b.priceRange];
        case 'popular':
          return b.popular - a.popular;
        case 'topChart':
          return a.topChart - b.topChart;
        default:
          return 0;
      }
    });

    return filtered;
  }, [restaurantsData, searchTerm, selectedCategory, selectedLocation, priceFilter, alphabetFilter, sortBy]);

  // Cart functions
  const addToCart = (item, quantity = 1, options = {}) => {
    const cartItem = {
      ...item,
      quantity,
      options,
      cartId: Date.now() + Math.random(),
      addedAt: new Date()
    };
    setCart(prevCart => [...prevCart, cartItem]);
  };

  const updateCartQuantity = (cartId, newQuantity) => {
    if (newQuantity === 0) {
      setCart(cart.filter(item => item.cartId !== cartId));
    } else {
      setCart(cart.map(item => 
        item.cartId === cartId ? { ...item, quantity: newQuantity } : item
      ));
    }
  };

  const removeFromCart = (cartId) => {
    setCart(cart.filter(item => item.cartId !== cartId));
  };

  const clearCart = () => setCart([]);

  const getCartTotal = () => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const getCartItemCount = () => {
    return cart.reduce((total, item) => total + item.quantity, 0);
  };

  const toggleFavorite = (restaurantId) => {
    setFavorites(prev => 
      prev.includes(restaurantId) 
        ? prev.filter(id => id !== restaurantId)
        : [...prev, restaurantId]
    );
  };

  // Get menu items for restaurant using imported data
  const getMenuForRestaurant = (restaurantCategory) => {
    return getMenuForCategory(restaurantCategory);
  };

  // Loading Screen Component
  const LoadingScreen = () => (
    <div className="loading-screen">
      <div className="loading-content">
        <div className="loading-logo">🍽️</div>
        <h2 className="loading-title">{t.appName}</h2>
        <div className="loading-spinner"></div>
        <p className="loading-text">Loading restaurants...</p>
      </div>
    </div>
  );



  // Menu View Component with Enhanced Styling
  const MenuView = () => {
    const menuItems = getMenuForRestaurant(selectedRestaurant.category);
    
    return (
      <div className="menu-view">
        {/* Enhanced Menu Header */}
        <div className="enhanced-menu-header">
          <div className="menu-header-content">
            <button 
              className="enhanced-back-button" 
              onClick={() => setCurrentView('directory')}
            >
              ← {t.back}
            </button>
            
            <div className="restaurant-info-enhanced">
              <h1>{selectedRestaurant.name}</h1>
              <p>{selectedRestaurant.chineseName}</p>
              <div className="restaurant-rating-info">
                <span className="rating-stars">⭐ {selectedRestaurant.rating}</span>
                <span className="review-count">({selectedRestaurant.reviewCount} {t.reviews})</span>
                <span className="price-indicator">{selectedRestaurant.priceRange}</span>
              </div>
            </div>
            
            <div className="user-info-compact">
              <img src={currentUser.avatar} alt="User" className="user-avatar-small" />
              <div className="user-details-compact">
                <p>{currentUser.name}</p>
                <p>📞 {currentUser.phone}</p>
              </div>
            </div>
          </div>
          
          {/* Restaurant Hero Image */}
          <div className="restaurant-hero">
            <img 
              src={selectedRestaurant.image} 
              alt={selectedRestaurant.name}
              className="restaurant-hero-image"
            />
            <div className="hero-overlay">
              <div className="delivery-info-overlay">
                <span className="delivery-time">🕒 {selectedRestaurant.deliveryTime}</span>
                <span className="delivery-fee">🚚 ${selectedRestaurant.deliveryFee}</span>
                <span className="distance">📍 {selectedRestaurant.distance}km</span>
              </div>
            </div>
          </div>
        </div>

        {/* Enhanced Menu Items */}
        <div className="enhanced-menu-items">
          {menuItems.map(item => (
            <div key={item.id} className="enhanced-menu-item">
              <div className="menu-item-image-container">
                <img 
                  src={item.image} 
                  alt={item.name}
                  className="enhanced-menu-item-image"
                />
                {item.spicy && <div className="spicy-indicator">🌶️</div>}
                {item.popular && <div className="popular-item-badge">{t.popular}</div>}
              </div>
              
              <div className="enhanced-menu-item-details">
                <div className="item-header">
                  <h3 className="item-name">{item.name}</h3>
                  <div className="item-price">${item.price}</div>
                </div>
                
                <p className="item-description">{item.description}</p>
                
                <div className="item-meta">
                  {item.category && <span className="item-category">{item.category}</span>}
                  {item.preparationTime && <span className="prep-time">⏱️ {item.preparationTime}</span>}
                </div>
                
                <button 
                  className="enhanced-add-to-cart-btn"
                  onClick={() => {
                    setSelectedMenuItem(item);
                    setShowItemOptions(true);
                  }}
                >
                  {t.addToCart}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Item Options Modal */}
        {showItemOptions && selectedMenuItem && (
          <div className="item-options-overlay">
            <div className="item-options-modal">
              <div className="modal-header">
                <h3>{selectedMenuItem.name}</h3>
                <button 
                  className="close-modal"
                  onClick={() => setShowItemOptions(false)}
                >
                  ✕
                </button>
              </div>
              
              <div className="modal-content">
                <img 
                  src={selectedMenuItem.image} 
                  alt={selectedMenuItem.name}
                  className="modal-item-image"
                />
                
                <div className="item-options">
                  <h4>{t.options}</h4>
                  <div className="option-group">
                    <label>Size:</label>
                    <div className="option-buttons">
                      <button className="option-btn active">Regular</button>
                      <button className="option-btn">Large (+$3)</button>
                    </div>
                  </div>
                  
                  <div className="option-group">
                    <label>Spice Level:</label>
                    <div className="option-buttons">
                      <button className="option-btn active">Mild</button>
                      <button className="option-btn">Medium</button>
                      <button className="option-btn">Hot</button>
                    </div>
                  </div>
                </div>
                
                <div className="quantity-selector">
                  <label>Quantity:</label>
                  <div className="quantity-controls">
                    <button className="quantity-btn">-</button>
                    <span className="quantity">1</span>
                    <button className="quantity-btn">+</button>
                  </div>
                </div>
                
                <div className="modal-actions">
                  <button 
                    className="confirm-add-btn"
                    onClick={() => {
                      addToCart(selectedMenuItem, 1, {});
                      setShowItemOptions(false);
                      setSelectedMenuItem(null);
                    }}
                  >
                    {t.confirm} - ${selectedMenuItem.price}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Takeaway Order Confirmation Modal */}
        {showOrderTypeSelector && selectedRestaurant && (
          <div className="item-options-overlay">
            <div className="item-options-modal">
              <div className="modal-header">
                <h3>{t.takeawayOrder}</h3>
                <button 
                  className="close-modal"
                  onClick={() => setShowOrderTypeSelector(false)}
                >
                  ✕
                </button>
              </div>
              
              <div className="modal-content">
                <div className="restaurant-info">
                  <h4>{selectedRestaurant.name}</h4>
                  <p className="restaurant-chinese">{selectedRestaurant.chineseName}</p>
                </div>
                
                <div className="takeaway-info">
                  <div className="takeaway-icon">🥡</div>
                  <h4>{t.orderTypeDesc}</h4>
                  <p>Order food for pickup and takeaway from this restaurant</p>
                  
                  <div className="takeaway-features">
                    <div className="feature-item">
                      <span className="feature-icon">⏰</span>
                      <span>Quick pickup time</span>
                    </div>
                    <div className="feature-item">
                      <span className="feature-icon">📱</span>
                      <span>Easy mobile ordering</span>
                    </div>
                    <div className="feature-item">
                      <span className="feature-icon">💳</span>
                      <span>Secure payment</span>
                    </div>
                  </div>
                </div>
                
                <div className="modal-actions">
                  <button 
                    className="confirm-add-btn takeaway-btn"
                    onClick={() => handleOrderNow(selectedRestaurant)}
                  >
                    {t.orderNow} 🥡
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Booking Modal */}
        {showBookingModal && selectedRestaurant && (
          <div className="item-options-overlay">
            <div className="item-options-modal">
              <div className="modal-header">
                <h3>{t.pickupBooking}</h3>
                <button 
                  className="close-modal"
                  onClick={() => setShowBookingModal(false)}
                >
                  ✕
                </button>
              </div>
              
              <div className="modal-content">
                <div className="restaurant-info">
                  <h4>{selectedRestaurant.name}</h4>
                  <p className="restaurant-chinese">{selectedRestaurant.chineseName}</p>
                </div>
                
                <div className="booking-form">
                  <h4>{t.bookingDetails}</h4>
                  
                  <div className="form-group">
                    <label>{t.customerName} *</label>
                    <input
                      type="text"
                      value={bookingData.name}
                      onChange={(e) => handleBookingChange('name', e.target.value)}
                      placeholder="Enter your name"
                      className="form-input"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>{t.phoneNumber} *</label>
                    <input
                      type="tel"
                      value={bookingData.phone}
                      onChange={(e) => handleBookingChange('phone', e.target.value)}
                      placeholder="Enter your phone number"
                      className="form-input"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>{t.pickupTime}</label>
                    <input
                      type="datetime-local"
                      value={bookingData.pickupTime}
                      onChange={(e) => handleBookingChange('pickupTime', e.target.value)}
                      min={new Date().toISOString().slice(0, 16)}
                      className="form-input"
                    />
                    <small>Leave empty for ASAP pickup</small>
                  </div>
                  
                  <div className="form-group">
                    <label>{t.specialInstructions}</label>
                    <textarea
                      value={bookingData.specialInstructions}
                      onChange={(e) => handleBookingChange('specialInstructions', e.target.value)}
                      placeholder="Any special instructions for your pickup..."
                      className="form-textarea"
                      rows="3"
                    />
                  </div>
                </div>
                
                <div className="modal-actions">
                  <button 
                    className="confirm-add-btn booking-btn"
                    onClick={confirmBooking}
                  >
                    {t.confirmBooking} 📅
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Enhanced Cart View
  const CartView = () => (
    <div className="cart-view">
      <div className="enhanced-cart-header">
        <div className="cart-header-content">
          <button 
            className="enhanced-back-button" 
            onClick={() => setCurrentView('directory')}
          >
            ← {t.back}
          </button>
          
          <h1>{t.cart} ({getCartItemCount()})</h1>
          
          {cart.length > 0 && (
            <button 
              className="clear-cart-button"
              onClick={clearCart}
            >
              {t.clearCart}
            </button>
          )}
        </div>
      </div>

      {cart.length === 0 ? (
        <div className="enhanced-empty-cart">
          <div className="empty-cart-icon">🛒</div>
          <h3>{t.emptyCart}</h3>
          <p>Discover delicious food from our restaurants</p>
          <button 
            className="start-shopping-btn"
            onClick={() => setCurrentView('directory')}
          >
            {t.startShopping}
          </button>
        </div>
      ) : (
        <>
          <div className="enhanced-cart-items">
            {cart.map(item => (
              <div key={item.cartId} className="enhanced-cart-item">
                <img 
                  src={item.image} 
                  alt={item.name}
                  className="cart-item-image"
                />
                
                <div className="cart-item-details">
                  <h3 className="cart-item-name">{item.name}</h3>
                  <p className="cart-item-restaurant">{item.restaurantName}</p>
                  <div className="cart-item-price">${item.price}</div>
                </div>
                
                <div className="cart-item-controls">
                  <div className="quantity-controls">
                    <button 
                      className="quantity-btn"
                      onClick={() => updateCartQuantity(item.cartId, item.quantity - 1)}
                    >
                      -
                    </button>
                    <span className="quantity">{item.quantity}</span>
                    <button 
                      className="quantity-btn"
                      onClick={() => updateCartQuantity(item.cartId, item.quantity + 1)}
                    >
                      +
                    </button>
                  </div>
                  
                  <div className="item-total">${(item.price * item.quantity).toFixed(2)}</div>
                  
                  <button 
                    className="remove-item-btn" 
                    onClick={() => removeFromCart(item.cartId)}
                  >
                    🗑️
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="enhanced-cart-summary">
            <div className="summary-details">
              <div className="summary-row">
                <span>Subtotal:</span>
                <span>${getCartTotal().toFixed(2)}</span>
              </div>
              <div className="summary-row">
                <span>Delivery Fee:</span>
                <span>$3.99</span>
              </div>
              <div className="summary-row">
                <span>Service Fee:</span>
                <span>$1.99</span>
              </div>
              <div className="summary-row total">
                <span>{t.total}:</span>
                <span>${(getCartTotal() + 3.99 + 1.99).toFixed(2)}</span>
              </div>
            </div>
            
            <button className="enhanced-checkout-btn">
              {t.checkout} - ${(getCartTotal() + 3.99 + 1.99).toFixed(2)}
            </button>
          </div>
        </>
      )}
    </div>
  );

  // Enhanced Favorites View
  const FavoritesView = () => {
    const favoriteRestaurants = restaurantsData.filter(r => favorites.includes(r.id));
    
    return (
      <div className="favorites-view">
        <div className="enhanced-favorites-header">
          <div className="favorites-header-content">
            <button 
              className="enhanced-back-button" 
              onClick={() => setCurrentView('directory')}
            >
              ← {t.back}
            </button>
            
            <h1>{t.favorites} ({favoriteRestaurants.length})</h1>
          </div>
        </div>

        {favoriteRestaurants.length === 0 ? (
          <div className="enhanced-empty-favorites">
            <div className="empty-favorites-icon">❤️</div>
            <h3>{t.emptyFavorites}</h3>
            <p>Save your favorite restaurants here</p>
            <button 
              className="browse-restaurants-btn"
              onClick={() => setCurrentView('directory')}
            >
              {t.browseFavorites}
            </button>
          </div>
        ) : (
          <div className="enhanced-favorites-list">
            {favoriteRestaurants.map(restaurant => (
              <div 
                key={restaurant.id} 
                className="enhanced-favorite-item"
                onClick={() => {
                  setSelectedRestaurant(restaurant);
                  setCurrentView('menu');
                }}
              >
                <img 
                  src={restaurant.image} 
                  alt={restaurant.name}
                  className="favorite-item-image"
                />
                
                <div className="favorite-item-details">
                  <h3 className="favorite-item-name">{restaurant.name}</h3>
                  <p className="favorite-item-chinese">{restaurant.chineseName}</p>
                  <div className="favorite-item-meta">
                    <span className="rating">⭐ {restaurant.rating}</span>
                    <span className="reviews">({restaurant.reviewCount} {t.reviews})</span>
                    <span className="price-range">{restaurant.priceRange}</span>
                  </div>
                  <div className="favorite-item-info">
                    <span>🕒 {restaurant.deliveryTime}</span>
                    <span>🚚 ${restaurant.deliveryFee}</span>
                    <span>📍 {restaurant.distance}km</span>
                  </div>
                </div>
                
                <button 
                  className="unfavorite-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleFavorite(restaurant.id);
                  }}
                >
                  💔
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Directory View Component - Enhanced with better styling
  const DirectoryView = () => (
    <div className="directory-view">
      {/* Professional Header */}
      <div className="professional-header">
        <div className="header-content">
          <div className="header-left">
            <div className="app-branding">
              <span className="brand-name">Learns2gether</span>
            </div>
          </div>
          <div className="header-right">
            <button className="header-filter-btn">⚙️</button>
            <button className="header-grid-btn">⊞</button>
          </div>
        </div>
        
        {/* User Welcome Section */}
        <div className="user-welcome-section">
          <div className="user-info">
            <img src={currentUser.avatar} alt="User" className="user-avatar" />
            <div className="user-details">
              <h2>歡迎回來, Ellis Chan</h2>
            </div>
          </div>
        </div>
      </div>

      {/* Professional Search Section */}
      <div className="professional-search-section">
        <div className="search-container">
          <div className="search-input-wrapper">
            <span className="search-icon">🔍</span>
            <input
              type="text"
              className="search-input"
              placeholder="搜尋餐廳或美食..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Professional Navigation Grid */}
      <div className="professional-nav-section">
        <div className="nav-grid-container">
          <div className="nav-card professional" onClick={() => setCurrentView('categories')}>
            <div className="nav-icon">📋</div>
            <span className="nav-label">分類</span>
          </div>
          <div className="nav-card professional" onClick={() => setCurrentView('alphabet')}>
            <div className="nav-icon">🔤</div>
            <span className="nav-label">A-Z</span>
          </div>
          <div className="nav-card professional" onClick={() => setShowFilters(!showFilters)}>
            <div className="nav-icon">📍</div>
            <span className="nav-label">地區</span>
          </div>
        </div>
      </div>

      {/* Professional Featured Section */}
      <div className="professional-featured-section">
        <div className="section-header">
          <h3 className="section-title">精選推薦</h3>
        </div>
        
        <div className="featured-cards-container">
          <div className="featured-card professional">
            <div className="featured-image-container">
              <img 
                src="https://images.unsplash.com/photo-1546833999-b9f581a1996d?w=400&h=200&fit=crop" 
                alt="今日熱門"
                className="featured-image"
              />
              <div className="discount-badges">
                <span className="discount-badge main">10% OFF</span>
                <span className="discount-badge secondary">30% OFF</span>
                <span className="discount-badge third">20% OFF</span>
                <span className="discount-badge fourth">15% OFF</span>
              </div>
            </div>
            <div className="featured-content">
              <h4 className="featured-title">今日熱門</h4>
              <p className="featured-subtitle">最受歡迎的料理</p>
              <p className="featured-description">每日精選人氣美食</p>
            </div>
          </div>
          
          <div className="featured-card professional">
            <div className="featured-image-container">
              <img 
                src="https://images.unsplash.com/photo-1551218808-94e220e084d2?w=400&h=200&fit=crop" 
                alt="早餐特惠"
                className="featured-image"
              />
              <div className="discount-badges">
                <span className="discount-badge main">10% OFF</span>
                <span className="discount-badge secondary">30% OFF</span>
                <span className="discount-badge third">20% OFF</span>
                <span className="discount-badge fourth">15% OFF</span>
              </div>
            </div>
            <div className="featured-content">
              <h4 className="featured-title">早餐特惠</h4>
              <p className="featured-subtitle">全日早餐8折優惠</p>
              <p className="featured-description">營養豐富的早餐選擇</p>
            </div>
          </div>

          <div className="featured-card professional">
            <div className="featured-image-container">
              <img 
                src="https://images.unsplash.com/photo-1571091718767-18b5b1457add?w=400&h=200&fit=crop" 
                alt="新店開張"
                className="featured-image"
              />
              <div className="discount-badges">
                <span className="discount-badge main">10% OFF</span>
                <span className="discount-badge secondary">30% OFF</span>
                <span className="discount-badge third">20% OFF</span>
                <span className="discount-badge fourth">15% OFF</span>
              </div>
            </div>
            <div className="featured-content">
              <h4 className="featured-title">新店開張</h4>
              <p className="featured-subtitle">首週全單9折</p>
              <p className="featured-description">歡迎品嚐新店好味道</p>
            </div>
          </div>

          <div className="featured-card professional">
            <div className="featured-image-container">
              <img 
                src="https://images.unsplash.com/photo-1579584425555-c3ce17fd4351?w=400&h=200&fit=crop" 
                alt="日式料理"
                className="featured-image"
              />
              <div className="discount-badges">
                <span className="discount-badge main">15% OFF</span>
                <span className="discount-badge secondary">25% OFF</span>
                <span className="discount-badge third">35% OFF</span>
                <span className="discount-badge fourth">20% OFF</span>
              </div>
            </div>
            <div className="featured-content">
              <h4 className="featured-title">日式料理</h4>
              <p className="featured-subtitle">新鮮壽司與拉麵</p>
              <p className="featured-description">正宗日本風味體驗</p>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Filters Panel */}
      {showFilters && (
        <div className="enhanced-filters-overlay">
          <div className="enhanced-filters-panel">
            <div className="filters-header">
              <h3>{t.filters}</h3>
              <button className="close-filters" onClick={() => setShowFilters(false)}>✕</button>
            </div>
            <div className="filters-content">
              <div className="filter-group">
                <label>{t.category}:</label>
                <select value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)}>
                  {categoriesWithAll.map(cat => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name} ({cat.count})
                    </option>
                  ))}
                </select>
              </div>
              <div className="filter-group">
                <label>{t.location}:</label>
                <select value={selectedLocation} onChange={(e) => setSelectedLocation(e.target.value)}>
                  {locationsWithAll.map(loc => (
                    <option key={loc.id} value={loc.id}>
                      {loc.name} ({loc.count})
                    </option>
                  ))}
                </select>
              </div>
              <div className="filter-group">
                <label>{t.priceRange}:</label>
                <select value={priceFilter} onChange={(e) => setPriceFilter(e.target.value)}>
                  <option value="all">{t.allPrices}</option>
                  <option value="$">{t.budget}</option>
                  <option value="$$">{t.moderate}</option>
                  <option value="$$$">{t.premium}</option>
                </select>
              </div>
              <div className="filter-group">
                <label>{t.sortBy}:</label>
                <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                  {sortOptions.map(option => (
                    <option key={option.id} value={option.id}>{option.name}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Enhanced Restaurant Results */}
      {(searchTerm || selectedCategory !== 'all' || selectedLocation !== 'all' || priceFilter !== 'all') && (
        <div className="enhanced-results-section">
          <div className="results-header">
            <h3>{t.restaurants} ({filteredRestaurants.length})</h3>
            <div className="view-toggle">
              <button 
                className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
                onClick={() => setViewMode('grid')}
              >
                ⊞
              </button>
              <button 
                className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
                onClick={() => setViewMode('list')}
              >
                ☰
              </button>
            </div>
          </div>
          
          <div className={`enhanced-restaurants-grid ${viewMode}`}>
            {filteredRestaurants.map(restaurant => (
              <div 
                key={restaurant.id} 
                className="enhanced-restaurant-card"
                onClick={() => {
                  setSelectedRestaurant(restaurant);
                  setCurrentView('menu');
                }}
              >
                <div className="card-image-container enhanced">
                  <img 
                    src={restaurant.image} 
                    alt={restaurant.name}
                    className="card-image"
                  />
                  <button 
                    className={`favorite-btn enhanced ${favorites.includes(restaurant.id) ? 'active' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleFavorite(restaurant.id);
                    }}
                  >
                    {favorites.includes(restaurant.id) ? '❤️' : '🤍'}
                  </button>
                  {restaurant.popular && <div className="popular-badge enhanced">{t.popular}</div>}
                  {restaurant.topChart <= 10 && <div className="top-badge enhanced">Top {restaurant.topChart}</div>}
                  <div className="rating-overlay">
                    <span className="rating-badge">⭐ {restaurant.rating}</span>
                  </div>
                </div>
                <div className="card-content enhanced">
                  <h4 className="restaurant-name">{restaurant.name}</h4>
                  <p className="restaurant-chinese">{restaurant.chineseName}</p>
                  <div className="restaurant-meta enhanced">
                    <span className="reviews">({restaurant.reviewCount} {t.reviews})</span>
                    <span className="price-range">{restaurant.priceRange}</span>
                  </div>
                  <div className="delivery-meta enhanced">
                    <span className="delivery-time">🕒 {restaurant.deliveryTime}</span>
                    <span className="delivery-fee">🚚 ${restaurant.deliveryFee}</span>
                    <span className="distance">📍 {restaurant.distance}km</span>
                  </div>
                  <div className="cuisine-tags enhanced">
                    {restaurant.cuisine.slice(0, 2).map((cuisine, index) => (
                      <span key={index} className="cuisine-tag enhanced">{cuisine}</span>
                    ))}
                  </div>
                  <div className="restaurant-actions">
                    <button 
                      className="order-now-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        showOrderTypeModal(restaurant);
                      }}
                    >
                      {t.orderNow}
                    </button>
                    <button 
                      className="book-pickup-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleBookPickup(restaurant);
                      }}
                    >
                      {t.bookPickup}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  // Categories View Component
  const CategoriesView = () => (
    <div className="categories-view">
      {/* Enhanced Header */}
      <div className="enhanced-categories-header">
        <div className="categories-header-content">
          <div className="enhanced-back-button" onClick={() => setCurrentView('directory')}>
            ← {t.back || 'Back'}
          </div>
          <div className="categories-info-enhanced">
            <h1>{t.categories || 'Browse Categories'}</h1>
            <p>{t.findYourFavorite || 'Find your favorite cuisine'}</p>
          </div>
          <div className="user-info-compact">
            <img src={currentUser.avatar} alt="User" className="user-avatar-small" />
            <div className="user-details-compact">
              <p>{currentUser.name}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Categories Grid */}
      <div className="enhanced-categories-content">
        <div className="enhanced-categories-grid">
          {categoriesWithAll.filter(cat => cat.id !== 'all').map(category => (
            <div 
              key={category.id} 
              className="enhanced-category-card"
              onClick={() => {
                setSelectedCategory(category.id);
                setCurrentView('directory');
              }}
            >
              <div className="category-card-image">
                <div className="category-emoji">{category.emoji}</div>
                <div className="category-overlay">
                  <span className="restaurant-count-badge">{category.count}</span>
                </div>
              </div>
              <div className="category-card-content">
                <h3 className="category-name">{category.name}</h3>
                <p className="category-chinese">{category.chineseName}</p>
                <div className="category-meta">
                  <span className="restaurant-count">{category.count} {t.restaurants || 'restaurants'}</span>
                  <span className="category-arrow">→</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // Alphabet View Component
  const AlphabetView = () => {
    const getFilteredRestaurants = () => {
      if (alphabetFilter === 'all') {
        return restaurantsData;
      }
      return restaurantsData.filter(r => {
        if (alphabetFilter === '0-9') {
          return /^[0-9]/.test(r.name);
        }
        return r.name.toUpperCase().startsWith(alphabetFilter);
      });
    };

    const filteredRestaurants = getFilteredRestaurants();

    return (
      <div className="alphabet-view">
        {/* Header */}
        <div className="alphabet-header">
          <div className="alphabet-header-content">
            <div className="alphabet-back-button" onClick={() => setCurrentView('directory')}>
              ←
            </div>
            <div className="alphabet-title">
              A-Z 排序
            </div>
          </div>
        </div>

        {/* Alphabet Navigation */}
        <div className="alphabet-nav">
          <div className="alphabet-nav-grid">
            <button 
              className={`alphabet-nav-btn ${alphabetFilter === 'all' ? 'active' : ''}`}
              onClick={() => setAlphabetFilter('all')}
            >
              全部
            </button>
            {alphabetSections.map(letter => (
              <button 
                key={letter}
                className={`alphabet-nav-btn ${alphabetFilter === letter ? 'active' : ''}`}
                onClick={() => setAlphabetFilter(letter)}
              >
                {letter}
              </button>
            ))}
          </div>
        </div>

        {/* Restaurant List */}
        <div className="alphabet-content">
          <div className="alphabet-section-info">
            <h3>所有餐廳</h3>
            <p>找到 {filteredRestaurants.length} 個餐廳</p>
          </div>
          
          <div className="alphabet-restaurant-list">
            {filteredRestaurants.map(restaurant => (
              <div 
                key={restaurant.id} 
                className="alphabet-restaurant-item"
                onClick={() => {
                  setSelectedRestaurant(restaurant);
                  setCurrentView('menu');
                }}
              >
                <div className="alphabet-restaurant-image">
                  <img src={restaurant.image} alt={restaurant.name} />
                </div>
                <div className="alphabet-restaurant-info">
                  <h4 className="alphabet-restaurant-name">{restaurant.name}</h4>
                  <p className="alphabet-restaurant-chinese">{restaurant.chineseName}</p>
                  <div className="alphabet-restaurant-meta">
                    <span className="alphabet-rating">⭐ {restaurant.rating}</span>
                    <span className="alphabet-location">• {restaurant.location}</span>
                  </div>
                </div>
                <div className="alphabet-restaurant-arrow">
                  →
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // Bottom Navigation Component
  const BottomNavigation = () => (
    <nav className="bottom-nav">
      <div 
        className={`nav-item ${currentView === 'directory' ? 'active' : ''}`}
        onClick={() => setCurrentView('directory')}
      >
        <span className="nav-icon">⌂</span>
        <span className="nav-label">{t.directories || 'Directory'}</span>
      </div>
      
      <div 
        className={`nav-item ${currentView === 'categories' ? 'active' : ''}`}
        onClick={() => setCurrentView('categories')}
      >
        <span className="nav-icon">⊞</span>
        <span className="nav-label">{t.categories || 'Categories'}</span>
      </div>
      
      <div 
        className={`nav-item ${currentView === 'cart' ? 'active' : ''}`}
        onClick={() => setCurrentView('cart')}
      >
        <span className="nav-icon">⊡</span>
        <span className="nav-label">{t.cart || 'Cart'}</span>
        {getCartItemCount() > 0 && (
          <span className="notification-badge">{getCartItemCount()}</span>
        )}
      </div>
      
      <div 
        className={`nav-item ${currentView === 'favorites' ? 'active' : ''}`}
        onClick={() => setCurrentView('favorites')}
      >
        <span className="nav-icon">♡</span>
        <span className="nav-label">{t.favorites || 'Favorites'}</span>
        {favorites.length > 0 && (
          <span className="notification-badge">{favorites.length}</span>
        )}
      </div>
      
      <div 
        className={`nav-item ${currentView === 'alphabet' ? 'active' : ''}`}
        onClick={() => setCurrentView('alphabet')}
      >
        <span className="nav-icon nav-icon-text">Aa</span>
        <span className="nav-label">A-Z</span>
      </div>
    </nav>
  );

  // Settings View Component
  const SettingsViewComponent = () => (
    <SettingsView 
      onBack={() => setCurrentView('directory')}
      t={t}
    />
  );

  // Main render
  const renderCurrentView = () => {
    switch (currentView) {
      case 'directory':
        return <DirectoryView />;
      case 'menu':
        return <MenuView />;
      case 'cart':
        return <CartView />;
      case 'categories':
        return <CategoriesView />;
      case 'favorites':
        return <FavoritesView />;
      case 'alphabet':
        return <AlphabetView />;
      case 'settings':
        return <SettingsViewComponent />;
      default:
        return <DirectoryView />;
    }
  };

  // Show loading screen while initializing
  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <div className="app" style={{
      background: theme.colors.background,
      fontFamily: theme.fonts.primary,
      color: theme.colors.textPrimary
    }}>
      <header className="app-header">
        <h1>🍽️ MemsEat</h1>
        <div className="header-actions">
          <span className="user-greeting">Hi, {currentUser.name}!</span>
        </div>
      </header>
      
      <main className="app-main">
        {renderCurrentView()}
      </main>
      
      <BottomNavigation />
    </div>
  );
};

// Main App Component with Theme Provider
const FoodDirectoryApp = () => {
  return (
    <ThemeProvider>
      <FoodDirectoryAppContent />
    </ThemeProvider>
  );
};

export default FoodDirectoryApp; 